import { IHistoryItem } from '@/framework';
import { Framework } from '@/framework/instance';
import { ReferenceLines } from '@/plugins/reference-lines';
import { traverseTrees } from '@/utils/tree-kit';
import { SizeUnitType } from '@/utils/unit-kit';
import {
    ILayerProps,
    IPoint,
    ISize,
    REFERENCE_LINE_EVENT_NAMES,
    ReferenceLine,
    ResizeOperateType,
} from '@/version-types';
import { QuickAdjustOperate } from '../operate/quick-adjust-operate';
import { QuickResizeOperate } from '../operate/quick-resize-operate';
import { historyQuickAdjustOperate, historyQuickResizeOperate } from '../type';

interface IStageHistoryQuickAdjustData {
    originIndex: number;
    oldSize: ISize;
    newSize: ISize;
    unit: SizeUnitType;
    operateType: ResizeOperateType;
    oldReferenceLines: ReferenceLine[];
    newReferenceLines: ReferenceLine[];
}

interface IStageHistoryQuickResizeData {
    originIndex: number;
    oldHeight: number;
    newHeight: number;
    oldReferenceLines: ReferenceLine[];
    newReferenceLines: ReferenceLine[];
}

/**
 * 单画布舞台快速调整
 */
export class QuickStageHistory extends Framework {
    private syncOriginPosition: Map<string, IPoint> = new Map();

    constructor(private id: string) {
        super(id);
        this.registerQuickAdjustOperate();
        this.registerQuickResizeOperate();
    }

    private registerQuickResizeOperate() {
        this.framework.history.register(historyQuickResizeOperate, {
            onRedoUndo: (historyItem: IHistoryItem<IStageHistoryQuickResizeData>, isRedo: boolean, isUndo: boolean) => {
                if (historyItem.isDirty) return;
                const { originIndex, oldHeight, newHeight, oldReferenceLines, newReferenceLines } =
                    historyItem.data.data;
                this.framework.operate.delActiveLayer();
                if (isUndo) {
                    this.framework.getInstance(QuickResizeOperate).doResize(originIndex, oldHeight);
                    this.framework.getInstance(ReferenceLines).setReferenceLines(oldReferenceLines);
                }
                if (isRedo) {
                    this.framework.getInstance(QuickResizeOperate).doResize(originIndex, newHeight);
                    this.framework.getInstance(ReferenceLines).setReferenceLines(newReferenceLines);
                }
                this.framework.stage.setActiveStageIndex(this.framework.convertOriginToStage(originIndex));
                this.framework.stage.setIsPickStage(true);
            },
            onAfterWrite: () => {
                return {
                    message: [],
                };
            },
            onAfterRedoUndo: (historyItem: IHistoryItem<IStageHistoryQuickResizeData>) => {
                return {
                    message: [historyItem.data.data],
                };
            },
            onBeforeRedoUndo: (historyItem: IHistoryItem<IStageHistoryQuickResizeData>, isRedo, isUndo, store) => {
                const { originIndex, oldHeight, newHeight } = historyItem.data.data;
                const size = this.framework.getSize(originIndex);
                if (isUndo) {
                    const isRender = this.framework.checkStageIsRender(originIndex);
                    historyItem.isDirty = !isRender || !(size.height === newHeight);
                    store.set(historyItem.cursor, {
                        isDirty: historyItem.isDirty,
                    });
                }
                if (isRedo) {
                    const beforeData = store.get(historyItem.cursor);
                    if (!beforeData) {
                        historyItem.isDirty = true;
                        return;
                    }
                    historyItem.isDirty = beforeData.isDirty;
                    if (!historyItem.isDirty) {
                        const isRender = this.framework.checkStageIsRender(originIndex);
                        historyItem.isDirty = !isRender || !(size.height === oldHeight);
                    }
                }
            },
            onMessage: (message: [IStageHistoryQuickResizeData], isRedo, isUndo, isWrite) => {
                if (isWrite) return;
                const { originIndex, oldHeight, newHeight, oldReferenceLines, newReferenceLines } = message[0];
                this.framework.operate.tryRemoveActiveLayer(originIndex);
                if (isUndo) {
                    this.framework.getInstance(QuickResizeOperate).doResize(originIndex, oldHeight);
                    this.framework.getInstance(ReferenceLines).setReferenceLines(oldReferenceLines);
                }
                if (isRedo) {
                    this.framework.getInstance(QuickResizeOperate).doResize(originIndex, newHeight);
                    this.framework.getInstance(ReferenceLines).setReferenceLines(newReferenceLines);
                }
            },
            onSend: (data: {
                originIndex: number;
                oldHeight: number;
                newHeight: number;
                isStart: boolean;
                isEnd: boolean;
            }) => {
                return {
                    message: [data.originIndex, data.oldHeight, data.newHeight, data.isStart, data.isEnd],
                    isThrottle: true,
                    force: data.isEnd,
                };
            },
            onReceive: (message: [number, number, number, boolean, boolean]) => {
                const [originIndex, oldHeight, newHeight, isStart, isEnd] = message;
                this.framework.operate.tryRemoveActiveLayer(originIndex);
                if (isStart) {
                    this.framework.events.emit(REFERENCE_LINE_EVENT_NAMES.SYNC_STAGE_RESIZE_START);
                }
                if (isEnd) {
                    this.framework.events.emit(REFERENCE_LINE_EVENT_NAMES.SYNC_STAGE_RESIZE_END);
                }
                if (!isStart && !isEnd) {
                    this.framework.events.emit(REFERENCE_LINE_EVENT_NAMES.SYNC_STAGE_RESIZING, {
                        newSize: newHeight,
                        oldSize: oldHeight,
                        operateType: ResizeOperateType.BC,
                        stageIndex: this.framework.convertOriginToStage(originIndex),
                    });
                }
                this.framework.getInstance(QuickResizeOperate).doResize(originIndex, newHeight);
            },
        });
    }

    private registerQuickAdjustOperate() {
        this.framework.history.register(historyQuickAdjustOperate, {
            onRedoUndo: (historyItem: IHistoryItem<IStageHistoryQuickAdjustData>, isRedo: boolean, isUndo: boolean) => {
                if (historyItem.isDirty) return;
                const { originIndex, oldSize, newSize, unit, operateType, oldReferenceLines, newReferenceLines } =
                    historyItem.data.data;
                this.framework.operate.delActiveLayer();
                this.framework.stage.setStageUnit(unit);
                if (isUndo) {
                    this.framework.getInstance(QuickAdjustOperate).doAdjust(originIndex, newSize, oldSize, operateType);
                    this.framework.getInstance(ReferenceLines).setReferenceLines(oldReferenceLines);
                }
                if (isRedo) {
                    this.framework.getInstance(QuickAdjustOperate).doAdjust(originIndex, oldSize, newSize, operateType);
                    this.framework.getInstance(ReferenceLines).setReferenceLines(newReferenceLines);
                }
            },
            onAfterWrite: () => {
                return {
                    message: [],
                };
            },
            onAfterRedoUndo: (historyItem: IHistoryItem<IStageHistoryQuickAdjustData>) => {
                return {
                    message: [historyItem.data.data],
                };
            },
            onBeforeRedoUndo: (historyItem: IHistoryItem<IStageHistoryQuickAdjustData>, isRedo, isUndo, store) => {
                const { originIndex, oldSize, newSize } = historyItem.data.data;
                const size = this.framework.getSize(originIndex);
                if (isUndo) {
                    const isRender = this.framework.checkStageIsRender(originIndex);
                    historyItem.isDirty =
                        !isRender || !(size.width === newSize.width && size.height === newSize.height);
                    store.set(historyItem.cursor, {
                        isDirty: historyItem.isDirty,
                    });
                }
                if (isRedo) {
                    const beforeData = store.get(historyItem.cursor);
                    if (!beforeData) {
                        historyItem.isDirty = true;
                        return;
                    }
                    historyItem.isDirty = beforeData.isDirty;
                    if (!historyItem.isDirty) {
                        const isRender = this.framework.checkStageIsRender(originIndex);
                        historyItem.isDirty =
                            !isRender || !(size.width === oldSize.width && size.height === oldSize.height);
                    }
                }
            },

            onMessage: (message: [IStageHistoryQuickAdjustData], isRedo, isUndo, isWrite) => {
                if (isWrite) return;
                this.syncOriginPosition.clear();
                const { originIndex, oldSize, newSize, unit, operateType, oldReferenceLines, newReferenceLines } =
                    message[0];
                this.framework.operate.tryRemoveActiveLayer(originIndex);
                this.framework.stage.setStageUnit(unit);
                if (isUndo) {
                    this.framework.getInstance(QuickAdjustOperate).doAdjust(originIndex, newSize, oldSize, operateType);
                    this.framework.getInstance(ReferenceLines).setReferenceLines(oldReferenceLines);
                }
                if (isRedo) {
                    this.framework.getInstance(QuickAdjustOperate).doAdjust(originIndex, oldSize, newSize, operateType);
                    this.framework.getInstance(ReferenceLines).setReferenceLines(newReferenceLines);
                }
            },
            onSend: (data: {
                originIndex: number;
                oldSize: ISize;
                newSize: ISize;
                adjustType: ResizeOperateType;
                isStart: boolean;
                isEnd: boolean;
            }) => {
                return {
                    message: [data.originIndex, data.oldSize, data.newSize, data.adjustType, data.isStart, data.isEnd],
                    isThrottle: true,
                    force: data.isEnd,
                };
            },
            onReceive: (message: [number, ISize, ISize, ResizeOperateType, boolean, boolean]) => {
                const [originIndex, oldSize, newSize, adjustType, isStart, isEnd] = message;
                this.framework.operate.tryRemoveActiveLayer(originIndex);
                if (isStart) {
                    this.syncOriginPosition.clear();
                    const layers = this.framework.getLayers(originIndex);
                    traverseTrees(layers, (layer: ILayerProps) => {
                        this.syncOriginPosition.set(layer.id, {
                            x: layer.left,
                            y: layer.top,
                        });
                    });
                    // 发送舞台尺寸变化前事件
                    this.framework.events.emit(REFERENCE_LINE_EVENT_NAMES.SYNC_STAGE_RESIZE_START);
                }
                if (isEnd) {
                    // 发送舞台尺寸变化后事件
                    this.framework.events.emit(REFERENCE_LINE_EVENT_NAMES.SYNC_STAGE_RESIZE_END);
                }
                if (!isStart && !isEnd) {
                    const isVertical = adjustType === ResizeOperateType.TC || adjustType === ResizeOperateType.BC;
                    this.framework.events.emit(REFERENCE_LINE_EVENT_NAMES.SYNC_STAGE_RESIZING, {
                        newSize: isVertical ? newSize.height : newSize.width,
                        oldSize: isVertical ? oldSize.height : oldSize.width,
                        operateType: adjustType,
                        stageIndex: this.framework.convertOriginToStage(originIndex),
                    });
                }
                this.framework
                    .getInstance(QuickAdjustOperate)
                    .doAdjust(originIndex, oldSize, newSize, adjustType, this.syncOriginPosition);
            },
        });
    }
}
